import { observer } from "mobx-react-lite"


import { AgentIterationMessage } from "@/components/AgentIteration/AgentIterationMessage.tsx"
import { VersionContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { AgentTextMessage } from "@/components/Chat/AgentTextMessage.tsx"
import { CreditsSpentMessage } from "@/components/Chat/CreditsSpentMessage.tsx"
import { FirstTaskGreetingMessage } from "@/components/Chat/FirstTaskGreetingMessage.tsx"
import { MaxFixAttemptsExceededMessage } from "@/components/Chat/NewProject/MaxFixAttemptsExceededMessage.tsx"
import { PersonalTokenFormMessage } from "@/components/Chat/NewProject/PersonalTokenFormMessage.tsx"
import { PreviewReadyMessage } from "@/components/Chat/NewProject/PreviewReadyMessage.tsx"
import { ProjectCreatedMessage } from "@/components/Chat/NewProject/ProjectCreatedMessage.tsx"
import { ProjectNameFormMessage } from "@/components/Chat/NewProject/ProjectNameFormMessage.tsx"
import { RepositoryAuthTypeSelectorMessage } from "@/components/Chat/NewProject/RepositoryAuthTypeSelectorMessage.tsx"
import { RepositoryMirroringStatusMessage } from "@/components/Chat/NewProject/RepositoryMirroringStatusMessage.tsx"
import { RetryAnonymousMirroring } from "@/components/Chat/NewProject/RetryAnonymousMirroring.tsx"
import { SecretsListMessage } from "@/components/Chat/NewProject/SecretsListMessage.tsx"
import { ServiceListMessage } from "@/components/Chat/NewProject/ServiceListMessage.tsx"
import { UnknownMessage } from "@/components/Chat/UnknownMessage.tsx"
import { UserActionMessage } from "@/components/Chat/UserActionMessage.tsx"
import { Version } from "@/components/Version/Version.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"

import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { AiChatMessageContent, UTaskProcessDetails } from "@/lib/client/generated/TypeDefinitions.ts"
import type { GitHubVersionInfo } from "@/routes/tasks/TaskViewScene/TaskVersionsModel.ts"
import type { IObservableValue } from "mobx"


export const AgentMessage = observer((props: {
  message: ChatMessageModel
  isAgentRunning: IObservableValue<boolean>
  currentVersionCommitId: IObservableValue<string | null>
  successfulVersionCommitIds: IObservableValue<Set<string>>
  gitHubVersionInfo: IObservableValue<GitHubVersionInfo | null>
  runningProcess: UTaskProcessDetails | null
  onVersionCheckout: (commitId: string) => Promise<void>
  onMarkSuccessful: (commitId: string, successful: boolean) => Promise<void>
}) => {
  const content = props.message.content
  const messageType = content.className

  const devMode = useDevMode()

  if (messageType === "AgentText") {
    return (
      <AgentTextMessage
        content={content as AiChatMessageContent.AgentText}
        isNew={props.message.isNew}
        isLast={props.message.isLast}
        isActive={props.message.active}
      />
    )
  }

  if (messageType === "UserAction") {
    return (
      <UserActionMessage
        content={content as AiChatMessageContent.UserAction}
        isActive={props.message.active}
        timestamp={props.message.timestamp}
      />
    )
  }

  if (messageType === "AgentIteration") {
    return (
      <AgentIterationMessage
        taskId={props.message.taskId}
        messageId={props.message.id}
        content={content as AiChatMessageContent.AgentIteration}
        isNew={props.message.isNew}
        isLast={props.message.isLast}
        isActive={props.message.active}
        isAgentRunning={props.isAgentRunning.get()}
        retryTaskProcess={props.message.onRetryProcess}
        continueTaskProcess={props.message.onContinueProcess}
        runningProcess={props.runningProcess}
      />
    )
  }

  if (messageType == "Version") {
    return (
      <VersionContainer isActive={props.message.active}>
        <Version
          number={content.number}
          commitId={content.commitId}
          isCurrent={content.commitId == props.currentVersionCommitId.get()}
          isSuccessful={props.successfulVersionCommitIds.get().has(content.commitId)}
          createdAt={content.createdAt}
          createdBy={content.author}
          title={content.title}
          subtitle={content.subtitle}
          reasoning={content.reasoning}
          isGenerating={content.isGenerating}
          gitHubVersionInfo={props.gitHubVersionInfo.get()}
          onCheckout={props.onVersionCheckout}
          onMarkSuccessful={props.onMarkSuccessful}
        />
      </VersionContainer>
    )
  }

  if (messageType === "CreditsSpent") {
    return devMode ? (
      <CreditsSpentMessage
        content={content as AiChatMessageContent.CreditsSpent}
        isActive={props.message.active}
      />
    ) : null
  }

  if (messageType == "ServiceList") {
    return (
      <ServiceListMessage
        taskId={props.message.taskId}
        content={content as AiChatMessageContent.InitRuntimeConfig.ServiceList}
        isActive={props.message.active}
        isAgentRunning={props.isAgentRunning}
      />
    )
  }

  if (messageType == "SecretList") {
    return (
      <SecretsListMessage
        taskId={props.message.taskId}
        content={content as AiChatMessageContent.InitRuntimeConfig.SecretList}
        isActive={props.message.active}
        messageId={props.message.id}
        isAgentRunning={props.isAgentRunning}
      />
    )
  }

  if (messageType === "RepositoryAuthTypeSelector") {
    return (
      <RepositoryAuthTypeSelectorMessage
        taskId={props.message.taskId}
        content={content as AiChatMessageContent.InitProject.RepositoryAuthTypeSelector}
        isActive={props.message.active}
      />
    )
  }

  if (messageType === "PersonalTokenForm") {
    return (
      <PersonalTokenFormMessage
        message={props.message}
        content={content as AiChatMessageContent.InitProject.PersonalTokenForm}
      />
    )
  }

  if (messageType === "ProjectNameForm") {
    return (
      <ProjectNameFormMessage
        taskId={props.message.taskId}
        content={content as AiChatMessageContent.InitProject.ProjectNameForm}
        isActive={props.message.active}
      />
    )
  }

  if (messageType === "ProjectCreated") {
    return (
      <ProjectCreatedMessage
        message={props.message}
        content={content as AiChatMessageContent.InitProject.ProjectCreated}
      />
    )
  }

  if (messageType === "RepositoryMirroringStarted") {
    return (
      <RepositoryMirroringStatusMessage
        content={content as AiChatMessageContent.InitProject.RepositoryMirroringStarted}
        isActive={props.message.active}
        isLast={props.message.isLast}
      />
    )
  }

  if (messageType === "PreviewReady") {
    return (
      <PreviewReadyMessage
        content={content as AiChatMessageContent.InitProject.PreviewReady}
        isActive={props.message.active}
      />
    )
  }

  if (messageType === "MaxFixAttemptsExceeded") {
    return (
      <MaxFixAttemptsExceededMessage
        content={content as AiChatMessageContent.InitProject.MaxFixAttemptsExceeded}
        isActive={props.message.active}
      />
    )
  }

  if (messageType === "RetryAnonymousMirroring") {
    return (
      <RetryAnonymousMirroring
        isLast={props.message.isLast}
      />
    )
  }

  if (messageType === "FirstTaskGreeting") {
    return (
      <FirstTaskGreetingMessage
        content={props.message.content as AiChatMessageContent.FirstTaskGreeting}
        isLast={props.message.isLast}
      />
    )
  }

  return <UnknownMessage message={props.message} />
})
